import { useEffect, useMemo, useRef, useState } from 'react'

import type { DecadePalaceDto, LuckDto } from '../../contracts/cycles'
import { LifeScaleChart, baziEndAge, currentBaziSequence, currentZiweiSequence } from '../components/viz/LifeScale'
import type { LifeScaleSelection } from '../components/viz/LifeScale'
import { elementColor } from '../colors'
import { yearsLabel } from '../dates'
import { haptic } from '../telegram'
import { useCycles } from './cyclesData'
import './cycles.css'

/**
 * Жизненная шкала: девяносто лет двумя дорожками. Смысл экрана — не «сколько
 * мне лет», а стыки: что уходит на границе главы и что входит. Поэтому карточка
 * под шкалой всегда сравнивает выбранный отрезок с предыдущим на той же дорожке.
 */
export function LifeScale() {
  const { data, error } = useCycles()
  const scroller = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState<LifeScaleSelection | null>(null)

  const life = data?.life ?? null

  // Первый показ ставит выбор на текущую главу — она же интересует человека.
  const initial = useMemo<LifeScaleSelection | null>(() => {
    if (!life) return null
    const bazi = currentBaziSequence(life.bazi, life.age)
    if (bazi !== null) return { track: 'bazi', sequence: bazi }
    const ziwei = currentZiweiSequence(life.ziwei, life.nominalAge)
    return ziwei === null ? null : { track: 'ziwei', sequence: ziwei }
  }, [life])

  // Шкала шире экрана: без этого человек видит младенчество, а не «сейчас».
  useEffect(() => {
    const node = scroller.current
    if (!node || !life) return
    node.scrollLeft = Math.max(0, (life.age / 90) * node.scrollWidth - node.clientWidth / 2)
  }, [life])

  if (error) return <div className="state">{error}</div>
  if (!data || !life) return <div className="state">Раскладываем главы…</div>

  const active = selected ?? initial

  return (
    <div className="screen cycles lifescale">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">шкала</div>
      </div>

      <h1 className="display">Жизненная шкала</h1>
      <div className="sub">
        {life.birthYear} год рождения · сейчас {yearsLabel(life.age)}
      </div>

      <div className="lifescale-scroll" ref={scroller}>
        <LifeScaleChart
          life={life}
          selected={active}
          onSelect={(next) => {
            haptic('light')
            setSelected(next)
          }}
        />
      </div>

      <div className="lifescale-legend">
        <span className="viz-legend-item">
          <span className="viz-legend-dot" style={{ background: 'var(--ochre)' }} />
          Такты Ба Цзы
        </span>
        {life.ziwei.length > 0 && (
          <span className="viz-legend-item">
            <span className="viz-legend-dot" style={{ background: 'var(--violet)' }} />
            Дворцы Цзы Вэй
          </span>
        )}
      </div>

      {active?.track === 'bazi' && <BaziChapter list={life.bazi} sequence={active.sequence} />}
      {active?.track === 'ziwei' && <ZiweiChapter list={life.ziwei} sequence={active.sequence} />}

      {life.ziwei.length === 0 && data.limitations.length > 0 && (
        <div className="quiet">
          {data.limitations.map((limitation) => (
            <p key={limitation.code}>{limitation.message}</p>
          ))}
        </div>
      )}
    </div>
  )
}

function BaziChapter({ list, sequence }: { list: LuckDto[]; sequence: number }) {
  const index = list.findIndex((luck) => luck.sequence === sequence)
  const luck = list[index]
  if (!luck) return null
  const previous = index > 0 ? list[index - 1] : undefined
  const endAge = baziEndAge(luck, list[index + 1])

  return (
    <div className="card period-card chapter-card">
      <div className="lbl" style={{ color: elementColor(luck.pillar.element) }}>
        Такт Ба Цзы
      </div>
      <p className="period-strong">
        {luck.pillar.stemRu} {luck.pillar.branchRu} · {luck.tenGod.nameRu}
      </p>
      <p className="dim">
        {luck.startYear}–{luck.endYear} · {luck.startAge}–{Math.max(luck.startAge, endAge - 1)} лет
      </p>
      <p>{luck.tenGodMeaning.essence}</p>
      <p>{luck.tenGodMeaning.period}</p>
      {luck.personal.length > 0 && (
        <ul className="period-personal">
          {luck.personal.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
      <div className="chapter-shift">
        {previous && (
          <p>
            <span className="lbl">Уходит</span> {previous.tenGod.nameRu}: {previous.tenGodMeaning.essence}
          </p>
        )}
        <p>
          <span className="lbl">Входит</span> {luck.tenGod.nameRu}: {luck.tenGodMeaning.period}
        </p>
      </div>
    </div>
  )
}

function ZiweiChapter({ list, sequence }: { list: DecadePalaceDto[]; sequence: number }) {
  const index = list.findIndex((palace) => palace.sequence === sequence)
  const palace = list[index]
  if (!palace) return null
  const previous = index > 0 ? list[index - 1] : undefined

  return (
    <div className="card period-card chapter-card">
      <div className="lbl" style={{ color: 'var(--violet)' }}>
        Дворец десятилетия
      </div>
      <p className="period-strong">{palace.palaceRu}</p>
      <p className="dim">
        {palace.startYear}–{palace.endYear} · {palace.startAge}–{palace.endAge} лет
      </p>
      <p>{palace.meaning.sphere}</p>
      <p>{palace.meaning.decade}</p>
      {palace.stars.length > 0 && (
        <div className="chips period-stars">
          {palace.stars.map((star) => (
            <span className="chip" key={star.id}>
              {star.nameRu}
              <span className="dim"> · {star.role}</span>
            </span>
          ))}
        </div>
      )}
      <div className="chapter-shift">
        {previous && (
          <p>
            <span className="lbl">Уходит</span> {previous.palaceRu}: {previous.meaning.decade}
          </p>
        )}
        <p>
          <span className="lbl">Входит</span> {palace.palaceRu}: {palace.meaning.decade}
        </p>
      </div>
    </div>
  )
}
